import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Sparkles, Loader2, Search, ShieldCheck } from 'lucide-react';
import { useCity } from '@/context/CityContext';
import { markSessionIntroduced } from '@/lib/session';
import { geoSearch } from '@/lib/api';
import { tap, confirm as hapticConfirm, error as hapticError } from '@/lib/haptics';
import { toast } from 'sonner';

const HERO_IMG = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=1600&auto=format&fit=crop';
// Compact "live" data chips that sit ABOVE the headline (no overlap)
const PILLS = [
  { label: 'Weather',    emoji: '☀️' },
  { label: 'Transit',    emoji: '🚇' },
  { label: 'Air',        emoji: '🌿' },
  { label: 'Currency',   emoji: '💱' },
  { label: 'Translate',  emoji: '🗣️' },
  { label: 'Essentials', emoji: '🎒' },
];

export default function Landing() {
  const navigate = useNavigate();
  const { detectAndSet, setHasConsent, setCity } = useCity();
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const onSearchChange = (val) => {
    setQ(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val || val.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try { const r = await geoSearch(val.trim()); setResults(r || []); }
      catch { setResults([]); }
      finally { setSearching(false); }
    }, 280);
  };

  const pickResult = (r) => {
    tap();
    setCity({ name: r.name, country: r.country, country_code: r.country_code, lat: r.lat, lon: r.lon });
    setHasConsent(true);
    markSessionIntroduced();
    navigate('/');
  };

  const useMyLocation = async () => {
    hapticConfirm();
    setBusy(true);
    try {
      const c = await detectAndSet();
      toast.success(`Located: ${c.name}`);
      markSessionIntroduced();
      navigate('/');
    } catch (e) {
      hapticError();
      const code = e?.code;
      const msg = code === 'denied' ? 'Permission denied — search a city instead.'
        : code === 'iframe_blocked' ? 'Open in a new tab to allow location.'
        : 'Location unavailable — search a city instead.';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const tryAI = (prefill) => {
    hapticConfirm();
    setHasConsent(true);
    markSessionIntroduced();
    navigate('/ai', { state: prefill ? { prefill } : undefined });
  };

  // Detect a question vs a city name — keeps the existing city search,
  // and surfaces an "Ask Trivly" CTA when the input clearly looks like a question.
  const looksLikeQuestion = (text) => {
    const t = (text || '').trim();
    if (!t) return false;
    if (t.endsWith('?')) return true;
    if (t.length > 40) return true;
    const startWords = /^(what|how|where|when|why|who|which|can|should|is|are|do|does|tell|show|find|give|recommend|plan|suggest|help|best)\b/i;
    return startWords.test(t);
  };
  const isQuestion = looksLikeQuestion(q);

  return (
    <div data-testid="landing-screen" className="relative min-h-dvh bg-[#F2F0E4]">
      {/* Brand row */}
      <div className="relative z-20 px-5 pt-5 flex items-center justify-between">
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl overflow-hidden shadow-[0_6px_18px_rgba(15,26,19,0.18)]">
            <img src="/brand/trivly-app-icon.png" alt="Trivly" className="w-full h-full object-cover" draggable={false} />
          </span>
          <span className="font-display text-[18px] font-bold text-[#0F1A13] tracking-tight">Trivly</span>
        </motion.div>
        <span className="gt-pill gt-pill-light text-[10.5px]"><ShieldCheck className="w-3 h-3 text-[#15291C]" /> No account needed</span>
      </div>

      {/* Hero photo card — circle-cropped like reference */}
      <div className="relative z-10 px-5 pt-5">
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full aspect-[3/2] rounded-[36px] overflow-hidden border border-[#0F1A13]/06 shadow-[0_18px_40px_rgba(15,26,19,0.14)]"
        >
          <img src={HERO_IMG} alt="Travel hero" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A13]/55 via-[#0F1A13]/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5">
            <p className="text-[10.5px] uppercase tracking-[0.32em] font-bold text-[#C4FF3D]">Plan less. Experience more.</p>
            <p className="font-display text-2xl text-white font-bold tracking-tight mt-1">Right now in your pocket.</p>
          </div>
          {/* lime sticker badge */}
          <div className="absolute top-4 right-4 w-12 h-12 rounded-full bg-[#C4FF3D] flex items-center justify-center shadow-[0_6px_16px_rgba(196,255,61,0.6)] gt-pulse-glow">
            <Sparkles className="w-5 h-5 text-[#15291C]" />
          </div>
        </motion.div>
      </div>

      {/* Headline (clean, no overlapping floats) */}
      <div className="relative z-10 px-5 pt-6">
        <motion.h1
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="font-display text-[40px] sm:text-5xl leading-[0.96] font-bold tracking-tight text-[#0F1A13]"
        >
          Your AI <span className="gt-text-gradient">survival&nbsp;layer</span><br />for any city on earth.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
          className="text-[14.5px] text-[#4A5A50] mt-3 leading-relaxed max-w-[34ch]"
        >
          Real-time weather, transit, currency, safety, and a friend-grade AI that actually knows where you are.
        </motion.p>

        {/* Live data pills row — horizontal scroll, NEVER overlaps copy */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-5 flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5"
          data-testid="landing-pills"
        >
          {PILLS.map((p, i) => (
            <motion.span
              key={p.label}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.35 + i * 0.04 }}
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-bold border border-[#0F1A13]/08 ${i === 0 ? 'bg-[#15291C] text-[#C4FF3D]' : 'bg-white text-[#0F1A13]'}`}
              data-testid={`landing-pill-${p.label.toLowerCase()}`}
            >
              <span>{p.emoji}</span> {p.label}
            </motion.span>
          ))}
        </motion.div>
      </div>

      {/* Search input */}
      <div className="relative z-10 px-5 pt-5">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} className="relative">
          <div className="bg-white rounded-full border border-[#0F1A13]/10 shadow-[0_6px_22px_rgba(15,26,19,0.06)] px-4 py-3 flex items-center gap-3" data-testid="landing-search-wrapper">
            <Search className="w-5 h-5 text-[#15291C] shrink-0" />
            <input
              value={q}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Where are you going?"
              className="flex-1 bg-transparent outline-none text-[#0F1A13] placeholder:text-[#7A8A80] text-[15px] font-medium"
              data-testid="landing-search-input"
            />
            {searching && <Loader2 className="w-4 h-4 text-[#15291C] animate-spin" />}
          </div>
          {results.length > 0 && (
            <div className="absolute left-0 right-0 mt-2 bg-white rounded-3xl border border-[#0F1A13]/08 shadow-[0_18px_40px_rgba(15,26,19,0.14)] overflow-hidden z-30" data-testid="landing-search-results">
              {results.slice(0, 6).map((r, i) => (
                <button
                  key={`${r.lat}-${r.lon}-${i}`}
                  type="button"
                  onClick={() => pickResult(r)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[#0F1A13]/04 transition-colors text-left border-b border-[#0F1A13]/05 last:border-0"
                  data-testid={`landing-search-result-${i}`}
                >
                  <MapPin className="w-4 h-4 text-[#15291C] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#0F1A13] text-sm font-semibold truncate">{r.name}</p>
                    <p className="text-[#7A8A80] text-xs truncate">{r.country}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#7A8A80]" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Smart AI fallback — INLINE below the search input (NOT absolute) so it
            never overlaps the page-flow CTAs underneath. */}
        {q.trim().length >= 3 && isQuestion && (
          <motion.button
            type="button"
            onClick={() => tryAI(q.trim())}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
            className="relative mt-3 w-full px-4 py-3 bg-[#15291C] rounded-3xl border border-[#15291C] shadow-[0_18px_40px_rgba(15,26,19,0.18)] flex items-center gap-3 text-left hover:bg-[#1F3A29] tap-bounce"
            data-testid="landing-ask-ai-cta"
          >
            <span className="w-8 h-8 rounded-lg overflow-hidden shrink-0">
              <img src="/brand/trivly-ai-tile.png" alt="" className="w-full h-full object-cover" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#C4FF3D]">Ask Trivly AI</p>
              <p className="text-white text-sm font-semibold truncate">"{q.trim()}"</p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#C4FF3D]" />
          </motion.button>
        )}
      </div>

      {/* CTAs */}
      <div className="relative z-10 px-5 pt-4 pb-6 flex flex-col gap-3">
        <motion.button
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}
          type="button" onClick={useMyLocation} disabled={busy}
          className="gt-btn-primary gt-focus tap-bounce disabled:opacity-70 flex items-center justify-center gap-2"
          data-testid="landing-explore-cta"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <MapPin className="w-5 h-5" />}
          <span>{busy ? 'Locating…' : 'Explore my city'}</span>
          {!busy && <ArrowRight className="w-4 h-4" />}
        </motion.button>
        <motion.button
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.6 }}
          type="button" onClick={tryAI}
          className="gt-btn-lime gt-focus tap-bounce flex items-center justify-center gap-2"
          data-testid="landing-ai-cta"
        >
          <Sparkles className="w-5 h-5" />
          <span>Try AI Travel Assistant</span>
        </motion.button>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.75 }}
          className="text-center text-[11px] text-[#7A8A80] mt-1"
        >
          By continuing you agree to our <a href="/legal/privacy" className="underline hover:text-[#15291C]">Privacy</a> and <a href="/legal/terms" className="underline hover:text-[#15291C]">Terms</a>.
        </motion.p>
      </div>
    </div>
  );
}
